/**
 * Stage language for the Run Theater's beat layer (chips + caption bar).
 * Doubles as `eventsToBeats`' default pack, so pure-module tests stay stable.
 */

import type { BeatStrings } from '../../../theater/beats'

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

export const beats = {
  gettingStarted: 'Getting started',
  reviewingBrief: 'Reviewing the brief',
  attachmentsChip: (n: number) => plural(n, 'attachment'),
  collectingAttachments: (n: number) => `Collecting ${plural(n, 'attachment')}`,
  referenceVolumesChip: (n: number) => plural(n, 'reference volume'),
  consultingLibrary: (n: number) => `Consulting the library — ${plural(n, 'reference volume')} off the shelf`,
  thinking: 'Thinking it through',
  writing: 'Writing',
  coffeeBreak: 'Long stretch — quick coffee, then back at it',
  retryChip: (attempt: number) => `retry #${attempt}`,
  retryCaption: (attempt: number) => `Hit a snag — retrying (attempt ${attempt})`,
  adjustingChip: 'adjusting',
  adjustingCaption: 'Reached for a tool that isn’t available — adjusting and carrying on',
  checkingNotesChip: 'checking notes',
  checkingNotesCaption: 'Checking the case files',
  noReplyInTime: 'No reply in time — proceeding with assumptions',
  runCancelled: 'Wrapping up — run cancelled',
  replyArrived: 'Your reply arrived',
  waitingForReply: 'Waiting for your reply',
  filesChip: (n: number) => plural(n, 'file'),
  packingFiles: (n: number) => `Packing ${plural(n, 'file')}`,
  delivered: 'Delivered',
  deliveredWithNotes: 'Delivered — with notes',
  runFailed: 'Run failed',
  runFailedWithReason: (reason: string) => `Run failed — ${reason}`,
  hasAQuestion: 'Has a question for you',
  briefingSpecialist: 'Briefing a specialist',
  callingChip: (server: string) => `calling ${server}`,
  onTheLine: (server: string) => `On the line with ${server}`,
  workbenchCaption: (task: string) => `At the workbench — ${task}`,
  toolLabels: {
    code_execution: { chip: 'building it', caption: 'At the workbench — building your delivery' },
    bash_code_execution: { chip: 'running commands', caption: 'At the workbench — running commands' },
    text_editor_code_execution: { chip: 'shaping files', caption: 'At the workbench — shaping the files' },
    str_replace_editor: { chip: 'shaping files', caption: 'At the workbench — shaping the files' },
    web_search: { chip: 'searching the web', caption: 'Searching the web for answers' },
    web_fetch: { chip: 'reading a page', caption: 'Reading up on an outside page' }
  },
  settingTheStage: 'Setting the stage',
  specialistsOnIt: (caption: string, n: number) => `${caption} · ${n} specialist${n === 1 ? '' : 's'} on it`,
  breakChip: 'coffee',
  welcomingSpecialists: 'Holding the door for the specialists',
  standupBriefing: 'Quick standup — handing over the brief'
} satisfies BeatStrings
